import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { getTotalCost, normalizeEstimatedCost } from '@common/functions'
import { truncateString } from '@common/functions/truncateString'
import { MintButton } from '@common/library'
import SelectQuantity from '@components/SelectQuantity'
import useMain from '@hooks/useMain'
import useNetwork from '@hooks/useNetwork'
import useNetworkWallet from '@hooks/useNetworkWallet'
import AlgorandNetwork from '@providers/AlgorandNetwork'
import EthereumNetwork from '@providers/EthereumNetwork'
import PolygonNetwork from '@providers/PolygonNetwork'
import {
    CheckoutButtonsContainer,
    CheckoutCol,
    CheckoutContainer,
    CheckoutItemBox,
    CheckoutLeftItem,
    CheckoutRightItem,
    CheckoutRightItemLight,
    CheckoutRow,
    CheckoutTitle,
    FeedbackItem,
} from '@styles/wallets/styles'
import { __EIGHT_DIGITS__, __MAX_ELEMENTS__, __ONE_MINUTE__ } from '@types'

export const PaymentsCheckout: React.FC = () => {
    const {
        state: {
            warningMessage,
            config,
            mainInit: { network, blockchain, maxPerTransaction },
            currentWallet,
        },
        operations: { setCurrentStep, setCurrentWallet },
    } = useMain()
    const {
        state: { contractName, currentMintPrice, selectedOption, noRenderAgain },
        operations: { setSelectedOption, handleChange },
    } = useNetwork()
    const {
        state: { balance, suggestedGasFees, estimatedTotalCost, loadingMessage, walletProvider, currentWalletAddress },
        operations: {
            handleMint,
            setEstimatedTotalCost,
            newEstimateGas,
            setTruncatedAddress,
            setWalletProvider,
            handleChangeAddress,
        },
    } = useNetworkWallet()

    const { t } = useTranslation()

    useEffect(() => {
        if (window.ethereum && window.ethereum.isMetaMask) window.ethereum.on('accountsChanged', handleChangeAddress)

        return () => {
            if (window.ethereum && window.ethereum.isMetaMask)
                window.ethereum.removeListener('accountsChanged', handleChangeAddress)
        }
    }, [window.ethereum])

    useEffect(() => {
        if (!network) setCurrentStep('payments-wallet')
    }, [network, setCurrentStep])

    React.useEffect(() => {
        if (noRenderAgain.current) {
            newEstimateGas()
            noRenderAgain.current = false
        }
        setInterval(async () => {
            if (!noRenderAgain.current) {
                noRenderAgain.current = true
            } else {
                await newEstimateGas()
                noRenderAgain.current = false
            }
        }, __ONE_MINUTE__)
    }, [noRenderAgain])

    React.useEffect(() => {
        if (walletProvider && currentWalletAddress) {
            setTruncatedAddress(truncateString(currentWalletAddress, __MAX_ELEMENTS__))
            setCurrentWallet(walletProvider.getCurrentWallet())
        }
    }, [currentWalletAddress, setCurrentWallet, walletProvider])

    React.useEffect(() => {
        selectedOption > maxPerTransaction && setSelectedOption(maxPerTransaction)
        selectedOption < 1 && setSelectedOption(1)
    }, [maxPerTransaction, selectedOption, setSelectedOption])

    React.useEffect(() => {
        warningMessage === 'Confirming payment...' && setCurrentStep(`confirming`)
    }, [loadingMessage, warningMessage, setCurrentStep])

    React.useEffect(() => {
        setEstimatedTotalCost(getTotalCost(currentMintPrice, suggestedGasFees))
    }, [currentMintPrice, selectedOption, setEstimatedTotalCost, suggestedGasFees])

    const walletProviderValue = () => {
        switch (network) {
            case 'algorand': {
                return new AlgorandNetwork(config.networks.algorand[blockchain])
            }
            case 'polygon':
                return new PolygonNetwork(config.networks.polygon[blockchain])
            default:
                return new EthereumNetwork(config.networks.ethereum[blockchain])
        }
    }

    const connectedWalletName = React.useMemo(() => {
        if (!currentWallet) return ''

        return currentWallet.charAt(0).toUpperCase() + currentWallet.slice(1)
    }, [currentWallet])

    const handleChangeWallet = React.useCallback(async () => {
        setWalletProvider(walletProviderValue())
        localStorage.removeItem('walletconnect')
        setCurrentStep('payments')
    }, [setCurrentStep, setWalletProvider])

    return (
        <CheckoutContainer>
            <CheckoutTitle>Checkout</CheckoutTitle>
            <CheckoutRow>
                <CheckoutLeftItem>Minting</CheckoutLeftItem>
                <CheckoutRightItem>{contractName}</CheckoutRightItem>
            </CheckoutRow>

            <CheckoutRow>
                <CheckoutLeftItem>Payment method</CheckoutLeftItem>

                <CheckoutRightItem>
                    {connectedWalletName} | <label onClick={handleChangeWallet}>Change</label>
                </CheckoutRightItem>
            </CheckoutRow>

            <CheckoutRow>
                <CheckoutLeftItem>Your funds</CheckoutLeftItem>

                <CheckoutRightItem>
                    <span id="checkout-funds">{`${Number.parseFloat(balance).toFixed(__EIGHT_DIGITS__)}`} </span>
                    <span id="checkout-current-blockchain"> {` ${config.networks[network][blockchain].currency}`}</span>
                </CheckoutRightItem>
            </CheckoutRow>

            {loadingMessage === `You don't have enough funds` && (
                <FeedbackItem>You don&rsquo;t have enough funds</FeedbackItem>
            )}

            <CheckoutRow alignCenter>
                <CheckoutCol flexStart>
                    <CheckoutLeftItem>Quantity</CheckoutLeftItem>
                    <CheckoutRightItemLight id="max-quantity-per-mint">{`Max ${maxPerTransaction} per mint`}</CheckoutRightItemLight>
                </CheckoutCol>
                <CheckoutItemBox>
                    <SelectQuantity
                        mintPrice={currentMintPrice}
                        maxPerTransaction={maxPerTransaction}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        suggestedGasFees={suggestedGasFees}
                        setEstimatedTotalCost={setEstimatedTotalCost}
                        handleChange={handleChange}
                    />
                </CheckoutItemBox>
            </CheckoutRow>
            <hr />
            <CheckoutRow>
                <CheckoutLeftItem>Subtotal</CheckoutLeftItem>
                <CheckoutCol>
                    <CheckoutRightItem>
                        <span id="checkout-subtotal">{currentMintPrice} </span>
                        <span> {` ${config.networks[network][blockchain].currency}`}</span>
                    </CheckoutRightItem>
                </CheckoutCol>
            </CheckoutRow>
            <CheckoutRow>
                <CheckoutLeftItem>
                    {t(`payments.wallet.${config.networks[network][blockchain].consensus}`)}
                </CheckoutLeftItem>
                <CheckoutCol>
                    <CheckoutRightItem>
                        <span id="checkout-transaction-fee">
                            {`${normalizeEstimatedCost(currentMintPrice, suggestedGasFees)}`}{' '}
                        </span>
                        <span id="checkout-transaction-blockchain">
                            {' '}
                            {` ${config.networks[network][blockchain].currency}`}
                        </span>
                    </CheckoutRightItem>
                </CheckoutCol>
            </CheckoutRow>
            <hr />
            <CheckoutRow>
                <CheckoutLeftItem>Total</CheckoutLeftItem>
                <CheckoutCol>
                    <CheckoutRightItem>
                        <span id="checkout-total">
                            {`${normalizeEstimatedCost(currentMintPrice, estimatedTotalCost)}`}{' '}
                        </span>
                        <span id="checkout-total-blockchain">
                            {' '}
                            {` ${config.networks[network][blockchain].currency}`}
                        </span>
                    </CheckoutRightItem>
                </CheckoutCol>
            </CheckoutRow>
            <CheckoutRow>
                <CheckoutButtonsContainer>
                    <MintButton
                        onClick={handleMint}
                        disabled={loadingMessage.includes('reject') || warningMessage !== ''}
                    >
                        Confirm payment
                    </MintButton>
                </CheckoutButtonsContainer>
            </CheckoutRow>
        </CheckoutContainer>
    )
}
